import type { Alert } from "../../types";
import { relTime } from "../../utils/format";
import { CheatsheetButton } from "../CheatsheetButton";
import { alertToCtx } from "../../cheatsheets/alertToCtx";

interface Props {
  alert: Alert | null;
  onClose: () => void;
}

const EXAMPLE_QUERY: Record<string, (a: Alert) => string> = {
  XIDErrorDetected: (a) =>
    `increase(DCGM_FI_DEV_XID_ERRORS{Hostname="${a.labels?.node ?? "*"}"}[1m]) > 0`,
  HighGPUTemperature: () => "DCGM_FI_DEV_GPU_TEMP > 85",
  NodeDown: () => 'up{job="node-exporter"} == 0',
  IBLinkDown: () => 'ib_port_state == 0',
  IBSymbolErrorSpike: () => 'increase(ib_port_symbol_errors_total[5m]) > 100',
  SlurmPartitionFullyAllocated: () =>
    "slurm_partition_allocated / slurm_partition_total == 1",
  LongQueueWait: () =>
    'time() - slurm_job_submit_time{state="PENDING"} > 1800',
  ECCSBEWarn: () =>
    'increase(DCGM_FI_DEV_ECC_SBE_AGG_TOTAL[1h]) > 3',
  GPUMemoryHigh: () =>
    "DCGM_FI_DEV_FB_USED / DCGM_FI_DEV_FB_TOTAL > 0.9",
};

const STEPS: Record<string, string[]> = {
  XIDErrorDetected: [
    "Confirm via `dmesg -T | grep -i xid` on the affected node",
    "Check `nvidia-smi -q` — look for Volatile ECC / PCIe / Fallen off",
    "XID 79 = GPU fell off PCIe bus. Non-recoverable from user-space.",
    "Drain the node via `scontrol update NodeName=<n> State=DRAIN Reason=xid79`",
    "Reboot and re-run NHC. If XID reproduces, file an RMA.",
  ],
  HighGPUTemperature: [
    "Check ambient inlet temp and neighboring GPU status",
    "Read fan curve: `nvidia-smi -q -d POWER,TEMPERATURE`",
    "If cluster-wide, investigate CRAC / chilled water loop",
    "Consider thermally capping with `nvidia-smi -pl <W>` as short-term relief",
  ],
  NodeDown: [
    "Ping node from a neighbor; if ICMP dead, go BMC/iDRAC/iLO",
    "Check switch port state; confirm not a link-layer flap",
    "Review last `journalctl -k` before the outage for OOM / thermal / MCE",
    "If recovered, re-enter Slurm: `scontrol update NodeName=<n> State=RESUME`",
  ],
  IBLinkDown: [
    "Check port state on both endpoints with `ibstatus`",
    "Inspect `ibdiagnet` for topology changes",
    "Re-seat cable; confirm transceiver health via `mlxcables`",
    "If SM-driven, check OpenSM log for subnet re-sweeps",
  ],
  IBSymbolErrorSpike: [
    "`perfquery -x <lid> <port>` — capture extended counters",
    "Symbol errors on a single port → dirty connector / failing cable",
    "Widespread spikes → possible SM instability or environmental issue",
    "Threshold reset: `perfquery -R` after root cause",
  ],
  SlurmPartitionFullyAllocated: [
    "Check current pending queue depth — is this expected load?",
    "Review `sinfo -p <part>` for any drain/down nodes hiding capacity",
    "Consider bumping QoS for priority users if saturation continues",
  ],
  LongQueueWait: [
    "Identify blocking job: `squeue --me --start`",
    "Check if reason=Resources vs Priority — different remediation paths",
    "Confirm backfill is running: `sdiag` → BF stats",
  ],
  GPUMemoryHigh: [
    "Inspect workload: activation checkpointing? ZeRO-3?",
    "Check for memory leak with `nvidia-smi --query-gpu=memory.used --format=csv -l 5`",
    "Scale batch or accumulate gradients as a quick mitigation",
  ],
  ECCSBEWarn: [
    "SBE = correctable, no data loss — informational trend",
    "Watch for DBE — those are fatal, workload must restart",
    "Over time SBE cluster on a cell can hint at aging HBM; schedule replacement",
  ],
};

export function RunbookModal({ alert, onClose }: Props) {
  if (!alert) return null;
  const color =
    alert.severity === "critical"
      ? "var(--rose)"
      : alert.severity === "warning"
      ? "var(--amber)"
      : "var(--violet)";
  const exampleQuery = EXAMPLE_QUERY[alert.name]?.(alert) ?? "";
  const steps = STEPS[alert.name] ?? ["Generic runbook — follow team playbook."];
  return (
    <div
      className="fade-in"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(4,7,12,0.6)",
        backdropFilter: "blur(3px)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          maxHeight: "86vh",
          background: "var(--bg-1)",
          border: `1px solid ${color}`,
          borderRadius: 8,
          boxShadow: "var(--shadow-2)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: color,
              borderRadius: 2,
              boxShadow: `0 0 10px ${color}`,
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <span
                className={`chip ${
                  alert.severity === "critical"
                    ? "crit"
                    : alert.severity === "warning"
                    ? "warn"
                    : "info"
                }`}
              >
                <span className="dot pulse" />
                {alert.severity.toUpperCase()}
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: "var(--fg-2)" }}
              >
                alertname="{alert.name}" team="{alert.team}"
              </span>
            </div>
            <div
              style={{ fontSize: 17, fontWeight: 500, marginBottom: 4 }}
            >
              {alert.summary}
            </div>
            <div
              style={{ fontSize: 12, color: "var(--fg-1)", lineHeight: 1.5 }}
            >
              {alert.description}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <CheatsheetButton ctx={alertToCtx(alert)} inline />
            <button className="btn xs ghost" onClick={onClose}>
              esc ✕
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div>
              <div className="kpi-label" style={{ marginBottom: 6 }}>
                Labels
              </div>
              <div
                style={{
                  background: "var(--bg-2)",
                  border: "1px solid var(--line)",
                  borderRadius: 4,
                  padding: 10,
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  lineHeight: 1.7,
                }}
              >
                {Object.entries(alert.labels).map(([k, v]) => (
                  <div key={k}>
                    <span style={{ color: "var(--violet)" }}>{k}</span>=
                    <span style={{ color: "var(--cyan)" }}>"{v}"</span>
                  </div>
                ))}
                <div>
                  <span style={{ color: "var(--violet)" }}>severity</span>=
                  <span style={{ color: "var(--cyan)" }}>"{alert.severity}"</span>
                </div>
                <div>
                  <span style={{ color: "var(--violet)" }}>team</span>=
                  <span style={{ color: "var(--cyan)" }}>"{alert.team}"</span>
                </div>
              </div>
            </div>
            <div>
              <div className="kpi-label" style={{ marginBottom: 6 }}>
                Status
              </div>
              <div
                style={{
                  background: "var(--bg-2)",
                  border: "1px solid var(--line)",
                  borderRadius: 4,
                  padding: 10,
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  lineHeight: 1.8,
                }}
              >
                <div>
                  <span className="u-xdim">fired_at</span>{" "}
                  <span>{new Date(alert.fired_at).toISOString().slice(0, 19)}Z</span>
                </div>
                <div>
                  <span className="u-xdim">duration</span>{" "}
                  <span>{relTime(alert.fired_at).replace(" ago", "")}</span>
                </div>
                <div>
                  <span className="u-xdim">value</span>{" "}
                  <span style={{ color }}>{alert.value}</span>
                </div>
                <div>
                  <span className="u-xdim">fingerprint</span>{" "}
                  <span
                    className="u-dim u-truncate"
                    style={{
                      display: "inline-block",
                      maxWidth: 180,
                      verticalAlign: "bottom",
                    }}
                  >
                    {alert.fingerprint}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div className="kpi-label" style={{ marginBottom: 6 }}>
              PromQL expression
            </div>
            <div
              style={{
                background: "#050810",
                border: "1px solid var(--line)",
                borderRadius: 4,
                padding: "10px 12px",
                fontFamily: "var(--mono)",
                fontSize: 11,
                color: "var(--cyan)",
                whiteSpace: "pre-wrap",
              }}
            >
              {exampleQuery}
            </div>
          </div>
          <div>
            <div className="kpi-label" style={{ marginBottom: 8 }}>
              Runbook · remediation steps
            </div>
            <ol
              style={{
                margin: 0,
                paddingLeft: 20,
                fontSize: 12,
                lineHeight: 1.7,
                color: "var(--fg-1)",
              }}
            >
              {steps.map((s, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--line)",
            background: "var(--bg-2)",
            display: "flex",
            gap: 8,
          }}
        >
          <a
            className="btn"
            href={alert.runbook ?? "#"}
            target="_blank"
            rel="noreferrer"
          >
            Open runbook ↗
          </a>
          <span style={{ flex: 1 }} />
          <button className="btn">Silence 2h</button>
          <button className="btn primary">Acknowledge</button>
        </div>
      </div>
    </div>
  );
}
